<script setup lang="ts">
import MIcon from "../MIcon";
import { Key } from "ts-key-enum";
import { computed, ref, watch } from "vue";
import { randomID } from "../../../utils/helpers";
import TransitionExpand from "../../transitions/TransitionExpand.vue";

const props = defineProps({
  id: String,

  title: String,
  // auto height
  auto: Boolean,

  transitionName: {
    type: String,
    default: "expand-medicus",
  },
  expanded: Boolean,

  borderless: Boolean,

  dense: Boolean,

  // once expanded, shows the content indented against a theme-blue vertical
  // rule (the same accent MBanner's info type uses), instead of the plain
  // bordered/borderless look. Has no effect while collapsed.
  info: Boolean,

  count: Number,

  hideHeader: Boolean,
});

const emit = defineEmits({
  expand: (_state: boolean) => true,
  "update:expanded": (_state: boolean) => true,
});

const expandedState = ref(false);
const expanderId = props.id || randomID("m-expander-");

watch(
  () => props.expanded,
  (v) => (expandedState.value = v),
  {
    immediate: true,
  },
);

function toggle() {
  expandedState.value = !expandedState.value;
  emit("expand", expandedState.value);
  emit("update:expanded", expandedState.value);
}

const ariaButton = computed(() => ({
  "aria-expanded": expandedState.value,
  "aria-controls": expanderId,
}));
</script>

<template>
  <div
    class="m-expander"
    :class="{
      bordered: !borderless,
      expanded: expandedState,
      'info-expanded': info && expandedState,
    }"
  >
    <slot
      v-if="!hideHeader"
      name="head"
      v-bind="{
        expanded: expandedState,
        toggle,
        ariaButton,
      }"
    >
      <div
        class="m-expander-head flex flex-row medicus-outline"
        :class="{
          'with-padding': !dense,
        }"
        v-bind="ariaButton"
        role="button"
        tabindex="0"
        @click="toggle"
        @keydown="(e) => (e.key === Key.Enter || e.key === ' ' ? toggle() : null)"
      >
        <span v-if="count" class="m-expander--counter">{{ count }}</span>

        <slot name="title" v-bind="{ expanded: expandedState, toggle, count, ariaButton }">
          <div class="flex-auto">
            <h4 class="normal-weight">{{ title }}</h4>
          </div>
        </slot>
        <slot name="expand-button">
          <m-icon v-if="expandedState" name="fa-solid fa-chevron-up" />
          <m-icon v-else name="fa-solid fa-chevron-down" />
        </slot>
      </div>
    </slot>
    <div :id="expanderId" class="m-expander-content">
      <transition-expand v-if="auto" :name="transitionName">
        <template v-if="expandedState">
          <slot />
        </template>
      </transition-expand>
      <transition v-else :name="transitionName">
        <template v-if="expandedState">
          <slot />
        </template>
      </transition>
    </div>
    <slot name="footer" />
  </div>
</template>

<style lang="scss">
.m-expander {
  display: flex;
  flex-direction: column;

  &.bordered {
    border-bottom: 1px solid var(--border-colour);
  }
  &.expanded {
    padding-bottom: var(--space-1-5);
  }

  // a small gap separates the content from the header, then the content's
  // own vertical extent (start to end, no more) gets a theme-blue rule on
  // its left, offset 1px further than the header's own 5px inset since
  // that's where a capital letter's rendered ink actually starts, not its
  // character box edge (checked with canvas text metrics against "E").
  &.info-expanded {
    .m-expander-content {
      margin-top: var(--space-2);
      margin-left: var(--space-1-5);
      padding-left: var(--space-2);
      padding-right: var(--space-1);
      border-left: 3px solid var(--theme-blue);
    }
  }

  > * {
    padding-left: var(--space-1);
    padding-right: var(--space-1);
  }

  .m-expander--counter {
    background-color: var(--grey-darker);
    padding: 0px var(--space-2);
    border-radius: var(--radius-xl);

    color: var(--grey-lightest);

    margin-right: var(--space-1-5);
    line-height: 150%;
  }

  .m-expander-head {
    cursor: pointer;
    display: flex;
    flex: 1 1 auto;
    align-items: center;

    color: var(--grey-darkest);

    border-radius: var(--radius-default);
    outline: none;

    h4.normal-weight {
      font-weight: normal;
    }

    &.with-padding {
      padding-top: var(--space-2-5);
      padding-bottom: var(--space-2-5);
    }

    > .m-icon,
    > .m-icon {
      color: var(--theme-blue) !important;
      margin-right: var(--space-2-5);
    }

    &:hover {
      background-color: var(--grey-lightest);
    }
  }
}
</style>
<style>
.expand-medicus-enter-active,
.expand-medicus-leave-active {
  transition: height 0.33s ease-in-out;
  overflow: hidden;
}

.expand-medicus-enter,
.expand-medicus-leave-to {
  height: 0;
}

.expand-medicus-fast-enter-active,
.expand-medicus-fast-leave-active {
  transition: height 0.1s ease-in-out;
  overflow: hidden;
}

.expand-medicus-fast-enter,
.expand-medicus-fast-leave-to {
  height: 0;
}
</style>
